import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  WritableSignal,
} from '@angular/core';
import { form, FormField, max, maxLength, min, required, validate } from '@angular/forms/signals';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  ContactDetector,
  PropertyApplicationCreationDto,
  PropertyApplicationDto,
  PropertyApplicationService,
  PropertyDto,
  PropertyId,
} from '@workspace/data-types';
import { AbstractSignalFormComponent, AlertAuraComponent, AuraColorType } from '@workspace/ng-aura';
import { PropertyDvfMarketReferenceComponent } from '../../property-dvf-market-reference/property-dvf-market-reference.component';
import { PropertyApplicationServicesSelectorComponent } from '../services-selector/property-application-services-selector.component';

export interface PropertyApplicationFormModel {
  message: string;
  commissionRate: number | null;
  services: PropertyApplicationService[];
  attractivePrice: number | null;
  recommendedPrice: number | null;
  heartStrokePrice: number | null;
}

@Component({
  selector: 'etxe-property-application-form',
  imports: [
    CommonModule,
    FormField,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    AlertAuraComponent,
    PropertyDvfMarketReferenceComponent,
    PropertyApplicationServicesSelectorComponent,
  ],
  templateUrl: './property-application-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyApplicationFormComponent extends AbstractSignalFormComponent<
  PropertyApplicationCreationDto,
  PropertyApplicationFormModel
> {
  property = input<PropertyDto>();
  propertyId = input<PropertyId>();
  isLoading = input<boolean>(false);
  isFinalOfferAdjustment = input<boolean>(false);
  initialApplication = input<PropertyApplicationDto | undefined>();
  initialCommissionRate = input<number | undefined>();

  submitForm = output<PropertyApplicationCreationDto>();
  cancelled = output<void>();

  readonly auraColorType = AuraColorType;
  readonly hasConfirmedWarning = signal<boolean>(false);

  override model: WritableSignal<PropertyApplicationFormModel> = signal<PropertyApplicationFormModel>({
    message: '',
    commissionRate: 3.5,
    services: [],
    attractivePrice: null,
    recommendedPrice: null,
    heartStrokePrice: null,
  });

  readonly surfaceHabitable = computed<number | null>(() => {
    return this.property()?.keyInfo?.surface?.habitable ?? null;
  });

  readonly sellingPriceEuros = computed<number | null>(() => {
    const p = this.property()?.financials?.sellingPriceCents;
    return p != null ? p / 100 : null;
  });

  readonly targetCommissionRate = computed<number | null>(() => {
    return this.property()?.mandate?.targetCommissionRate ?? null;
  });

  readonly effectiveInitialCommissionRate = computed<number | null>(() => {
    const directRate = this.initialCommissionRate();
    if (directRate != null) {
      return directRate;
    }
    const appRate = this.initialApplication()?.engagement?.initialCommissionRate;
    if (appRate != null) {
      return appRate;
    }
    const dtoRate = this.dto()?.engagement?.initialCommissionRate;
    if (this.isFinalOfferAdjustment() && dtoRate != null) {
      return dtoRate;
    }
    return null;
  });

  readonly minAllowedCommissionRate = computed<number | null>(() => {
    const init = this.effectiveInitialCommissionRate();
    if (init == null) return null;
    return Math.max(0.1, Number((init - 1).toFixed(2)));
  });

  readonly maxAllowedCommissionRate = computed<number | null>(() => {
    const init = this.effectiveInitialCommissionRate();
    if (init == null) return null;
    return Number((init + 1).toFixed(2));
  });

  readonly commissionRateDelta = computed<number | null>(() => {
    const current = this.model().commissionRate;
    const init = this.effectiveInitialCommissionRate();
    if (current == null || init == null) return null;
    return Number((current - init).toFixed(2));
  });

  readonly isCommissionRateOutOfRange = computed<boolean>(() => {
    if (!this.isFinalOfferAdjustment()) return false;
    const current = this.model().commissionRate;
    const minRate = this.minAllowedCommissionRate();
    const maxRate = this.maxAllowedCommissionRate();
    if (current == null || minRate == null || maxRate == null) return false;
    return current < minRate || current > maxRate;
  });

  readonly effectiveRecommendedPrice = computed<number | null>(() => {
    if (this.isFinalOfferAdjustment()) {
      return this.model().recommendedPrice ?? this.sellingPriceEuros();
    }
    return this.sellingPriceEuros();
  });

  readonly netVendeurEuros = computed<number | null>(() => {
    return this.effectiveRecommendedPrice();
  });

  readonly estimatedCommissionEuros = computed<number | null>(() => {
    const price = this.effectiveRecommendedPrice();
    const rate = this.model().commissionRate;
    if (price == null || rate == null) return null;
    return Math.round((price * rate) / 100);
  });

  readonly initialEstimatedCommissionEuros = computed<number | null>(() => {
    const price = this.effectiveRecommendedPrice();
    const rate = this.effectiveInitialCommissionRate();
    if (price == null || rate == null) return null;
    return Math.round((price * rate) / 100);
  });

  readonly commissionEurosDelta = computed<number | null>(() => {
    const currentComm = this.estimatedCommissionEuros();
    const initComm = this.initialEstimatedCommissionEuros();
    if (currentComm == null || initComm == null) return null;
    return currentComm - initComm;
  });

  readonly totalFaiEuros = computed<number | null>(() => {
    const price = this.effectiveRecommendedPrice();
    const comm = this.estimatedCommissionEuros();
    if (price == null) return null;
    return comm != null ? price + comm : price;
  });

  adjustCommissionRate(step: number): void {
    const current = this.model().commissionRate ?? this.effectiveInitialCommissionRate() ?? 3.5;
    const next = Number((current + step).toFixed(1));
    const minR = this.minAllowedCommissionRate() ?? 0.1;
    const maxR = this.maxAllowedCommissionRate() ?? 25;
    if (next >= minR && next <= maxR) {
      this.model.update((m) => ({ ...m, commissionRate: next }));
    }
  }

  isAdjustDisabled(step: number): boolean {
    const current = this.model().commissionRate ?? this.effectiveInitialCommissionRate() ?? 3.5;
    const next = Number((current + step).toFixed(1));
    const minR = this.minAllowedCommissionRate() ?? 0.1;
    const maxR = this.maxAllowedCommissionRate() ?? 25;
    return next < minR || next > maxR || this.isLoading() || this.allDisable();
  }

  readonly pricePerSquareMeter = computed<number | null>(() => {
    const total = this.totalFaiEuros();
    const surface = this.surfaceHabitable();
    if (total == null || surface == null || surface <= 0) return null;
    return Math.round(total / surface);
  });

  readonly attractivePriceM2 = computed<number | null>(() => {
    const price = this.model().attractivePrice;
    const surface = this.surfaceHabitable();
    if (!price || !surface || surface <= 0) return null;
    return Math.round(price / surface);
  });

  readonly recommendedPriceM2 = computed<number | null>(() => {
    const price = this.model().recommendedPrice;
    const surface = this.surfaceHabitable();
    if (!price || !surface || surface <= 0) return null;
    return Math.round(price / surface);
  });

  readonly heartStrokePriceM2 = computed<number | null>(() => {
    const price = this.model().heartStrokePrice;
    const surface = this.surfaceHabitable();
    if (!price || !surface || surface <= 0) return null;
    return Math.round(price / surface);
  });

  readonly attractivePricePercentDiff = computed<number | null>(() => {
    const attr = this.model().attractivePrice;
    const rec = this.model().recommendedPrice;
    if (!attr || !rec || rec <= 0) return null;
    return Number((((attr - rec) / rec) * 100).toFixed(1));
  });

  readonly heartStrokePricePercentDiff = computed<number | null>(() => {
    const heart = this.model().heartStrokePrice;
    const rec = this.model().recommendedPrice;
    if (!heart || !rec || rec <= 0) return null;
    return Number((((heart - rec) / rec) * 100).toFixed(1));
  });

  readonly recommendedPriceVsOwnerPercentDiff = computed<number | null>(() => {
    const rec = this.model().recommendedPrice;
    const ownerPrice = this.sellingPriceEuros();
    if (!rec || !ownerPrice || ownerPrice <= 0) return null;
    return Number((((rec - ownerPrice) / ownerPrice) * 100).toFixed(1));
  });

  readonly minAttractivePrice = computed<number | null>(() => {
    const rec = this.model().recommendedPrice ?? this.sellingPriceEuros();
    if (!rec || rec <= 0) return null;
    return Math.round((rec * 0.85) / 1000) * 1000;
  });

  readonly maxAttractivePrice = computed<number | null>(() => {
    const rec = this.model().recommendedPrice ?? this.sellingPriceEuros();
    if (!rec || rec <= 0) return null;
    return Math.max(1000, rec - 1000);
  });

  readonly minHeartStrokePrice = computed<number | null>(() => {
    const rec = this.model().recommendedPrice ?? this.sellingPriceEuros();
    if (!rec || rec <= 0) return null;
    return rec + 1000;
  });

  readonly maxHeartStrokePrice = computed<number | null>(() => {
    const rec = this.model().recommendedPrice ?? this.sellingPriceEuros();
    if (!rec || rec <= 0) return null;
    return Math.round((rec * 1.15) / 1000) * 1000;
  });

  readonly isAttractivePriceInvalid = computed<boolean>(() => {
    if (!this.isFinalOfferAdjustment()) return false;
    const attr = this.model().attractivePrice;
    const rec = this.model().recommendedPrice;
    if (attr == null || rec == null) return false;
    const minAttr = this.minAttractivePrice();
    return attr >= rec || (minAttr != null && attr < minAttr);
  });

  readonly attractivePriceErrorMessage = computed<string | null>(() => {
    if (!this.isFinalOfferAdjustment()) return null;
    const attr = this.model().attractivePrice;
    const rec = this.model().recommendedPrice;
    if (attr == null || rec == null) return null;
    if (attr >= rec) {
      return 'Doit être strictement inférieur au prix conseillé';
    }
    const minAttr = this.minAttractivePrice();
    if (minAttr != null && attr < minAttr) {
      return `Ne peut pas être inférieur à -15 % (min. ${minAttr.toLocaleString('fr-FR')} €)`;
    }
    return null;
  });

  readonly isHeartStrokePriceInvalid = computed<boolean>(() => {
    if (!this.isFinalOfferAdjustment()) return false;
    const heart = this.model().heartStrokePrice;
    const rec = this.model().recommendedPrice;
    if (heart == null || rec == null) return false;
    const maxHeart = this.maxHeartStrokePrice();
    return heart <= rec || (maxHeart != null && heart > maxHeart);
  });

  readonly heartStrokePriceErrorMessage = computed<string | null>(() => {
    if (!this.isFinalOfferAdjustment()) return null;
    const heart = this.model().heartStrokePrice;
    const rec = this.model().recommendedPrice;
    if (heart == null || rec == null) return null;
    if (heart <= rec) {
      return 'Doit être strictement supérieur au prix conseillé';
    }
    const maxHeart = this.maxHeartStrokePrice();
    if (maxHeart != null && heart > maxHeart) {
      return `Ne peut pas dépasser +15 % (max. ${maxHeart.toLocaleString('fr-FR')} €)`;
    }
    return null;
  });

  readonly hasContactInMessage = computed(() => {
    const text = this.model().message || '';
    return ContactDetector.detect(text).hasContact;
  });

  readonly messageLength = computed<number>(() => {
    return (this.model().message || '').length;
  });

  readonly isSubmitDisabled = computed(() => {
    if (this.formTree().invalid() || this.isLoading()) {
      return true;
    }
    if (this.hasContactInMessage() && !this.hasConfirmedWarning()) {
      return true;
    }
    if (this.isFinalOfferAdjustment()) {
      const msgTrimmed = (this.model().message || '').trim();
      const msgRaw = (this.model().message || '').length;
      if (msgTrimmed.length < 200 || msgRaw > 2000) {
        return true;
      }
      const attr = this.model().attractivePrice;
      const rec = this.model().recommendedPrice;
      const heart = this.model().heartStrokePrice;
      if (attr == null || attr <= 0) {
        return true;
      }
      if (rec == null || rec <= 0) {
        return true;
      }
      if (heart == null || heart <= 0) {
        return true;
      }
      if (this.isAttractivePriceInvalid() || this.isHeartStrokePriceInvalid()) {
        return true;
      }
      if (this.isCommissionRateOutOfRange()) {
        return true;
      }
    }
    return false;
  });

  constructor() {
    super();
    effect(() => {
      if (!this.hasContactInMessage()) {
        this.hasConfirmedWarning.set(false);
      }
    });
  }

  confirmSendAnyway(): void {
    this.hasConfirmedWarning.set(true);
  }

  onPriceKeyDown(
    field: 'attractivePrice' | 'recommendedPrice' | 'heartStrokePrice',
    event: KeyboardEvent,
  ): void {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
      return;
    }

    event.preventDefault();
    const isUp = event.key === 'ArrowUp';
    const step = event.shiftKey ? 5000 : 1000;
    const current = this.model()[field];

    if (field === 'recommendedPrice') {
      const base = this.sellingPriceEuros() ?? 200000;
      if (current == null || Number.isNaN(current)) {
        this.model.update((m) => ({ ...m, recommendedPrice: base }));
      } else {
        const next = isUp ? current + step : Math.max(1000, current - step);
        this.model.update((m) => ({ ...m, recommendedPrice: next }));
      }
      return;
    }

    if (field === 'attractivePrice') {
      const minVal = this.minAttractivePrice() ?? 1000;
      const maxVal = this.maxAttractivePrice() ?? minVal;
      if (current == null || Number.isNaN(current)) {
        const initial = isUp ? minVal : maxVal;
        this.model.update((m) => ({ ...m, attractivePrice: initial }));
      } else {
        const next = isUp ? Math.min(maxVal, current + step) : Math.max(minVal, current - step);
        this.model.update((m) => ({ ...m, attractivePrice: next }));
      }
      return;
    }

    if (field === 'heartStrokePrice') {
      const minVal = this.minHeartStrokePrice() ?? 1000;
      const maxVal = this.maxHeartStrokePrice() ?? minVal;
      if (current == null || Number.isNaN(current)) {
        const initial = isUp ? minVal : maxVal;
        this.model.update((m) => ({ ...m, heartStrokePrice: initial }));
      } else {
        const next = isUp ? Math.min(maxVal, current + step) : Math.max(minVal, current - step);
        this.model.update((m) => ({ ...m, heartStrokePrice: next }));
      }
      return;
    }
  }

  override formTree = form(this.model, (b) => {
    required(b.message);
    maxLength(b.message, 2000);
    validate(b.message, ({ value }) => {
      const v = value() ?? '';
      const trimmed = v.trim();
      const raw = v.length;
      if (this.isFinalOfferAdjustment()) {
        if (trimmed.length < 200) {
          return {
            kind: 'minLengthFinalOffer',
            message: "L'analyse du bien doit comporter au moins 200 caractères",
          };
        }
        if (raw > 2000) {
          return {
            kind: 'maxLengthFinalOffer',
            message: "L'analyse du bien ne doit pas dépasser 2000 caractères",
          };
        }
      } else {
        if (trimmed.length < 10) {
          return {
            kind: 'minLengthMessage',
            message: 'Le message doit comporter au moins 10 caractères',
          };
        }
        if (raw > 2000) {
          return {
            kind: 'maxLengthMessage',
            message: 'Le message ne doit pas dépasser 2000 caractères',
          };
        }
      }
      return null;
    });
    required(b.commissionRate);
    min(b.commissionRate, 0.1);
    max(b.commissionRate, 25);
    validate(b.commissionRate, ({ value }) => {
      if (!this.isFinalOfferAdjustment()) {
        return null;
      }
      const minR = this.minAllowedCommissionRate();
      const maxR = this.maxAllowedCommissionRate();
      const initR = this.effectiveInitialCommissionRate();
      const v = value();
      if (v != null && minR != null && maxR != null && (v < minR || v > maxR)) {
        return {
          kind: 'outOfAllowedRange',
          message: `Le taux doit être compris entre ${minR} % et ${maxR} % (±1 pt par rapport à votre offre initiale de ${initR} %)`,
        };
      }
      return null;
    });

    required(b.attractivePrice, { when: () => this.isFinalOfferAdjustment() });
    min(b.attractivePrice, 1000);
    validate(b.attractivePrice, ({ value }) => {
      if (!this.isFinalOfferAdjustment()) {
        return null;
      }
      const v = value();
      const rec = this.model().recommendedPrice;
      if (v != null && rec != null) {
        if (v >= rec) {
          return {
            kind: 'mustBeBelowRecommended',
            message: 'Le prix attractif doit être strictement inférieur au prix conseillé',
          };
        }
        const minAttr = this.minAttractivePrice();
        if (minAttr != null && v < minAttr) {
          return {
            kind: 'mustNotBeBelowMinus15',
            message: `Le prix attractif ne peut pas être inférieur à -15 % du prix conseillé (min. ${minAttr.toLocaleString('fr-FR')} €)`,
          };
        }
      }
      return null;
    });

    required(b.recommendedPrice, { when: () => this.isFinalOfferAdjustment() });
    min(b.recommendedPrice, 1000);

    required(b.heartStrokePrice, { when: () => this.isFinalOfferAdjustment() });
    min(b.heartStrokePrice, 1000);
    validate(b.heartStrokePrice, ({ value }) => {
      if (!this.isFinalOfferAdjustment()) {
        return null;
      }
      const v = value();
      const rec = this.model().recommendedPrice;
      if (v != null && rec != null) {
        if (v <= rec) {
          return {
            kind: 'mustBeAboveRecommended',
            message: 'Le prix coup de cœur doit être strictement supérieur au prix conseillé',
          };
        }
        const maxHeart = this.maxHeartStrokePrice();
        if (maxHeart != null && v > maxHeart) {
          return {
            kind: 'mustNotExceedPlus15',
            message: `Le prix coup de cœur ne peut pas dépasser +15 % du prix conseillé (max. ${maxHeart.toLocaleString('fr-FR')} €)`,
          };
        }
      }
      return null;
    });
  });

  protected override buildModelFromDto(dto: PropertyApplicationCreationDto): PropertyApplicationFormModel {
    const prop = this.property();
    const defaultRate = prop?.mandate?.targetCommissionRate ?? 3.5;
    const defaultPrice =
      prop?.financials?.sellingPriceCents != null ? prop.financials.sellingPriceCents / 100 : null;
    const valuation = dto?.engagement?.valuation;
    const initialServices = (dto?.engagement?.services ??
      this.initialApplication()?.engagement?.services ??
      []) as PropertyApplicationService[];

    return {
      message: dto?.message ?? '',
      commissionRate: dto?.engagement?.commissionRate ?? defaultRate,
      services: initialServices,
      attractivePrice: valuation?.attractivePrice ?? null,
      recommendedPrice: valuation?.recommendedPrice ?? (this.isFinalOfferAdjustment() ? defaultPrice : null),
      heartStrokePrice: valuation?.heartStrokePrice ?? null,
    };
  }

  onServicesChange(services: PropertyApplicationService[]): void {
    this.model.update((m) => ({ ...m, services }));
  }

  protected override buildDtoFromModel(
    modelValue: PropertyApplicationFormModel,
  ): PropertyApplicationCreationDto {
    const propId = this.propertyId() ?? this.dto()?.propertyId ?? ('' as unknown as PropertyId);
    return {
      propertyId: propId,
      engagement: {
        commissionRate: modelValue.commissionRate ?? 3.5,
        services: modelValue.services ?? [],
        valuation:
          this.isFinalOfferAdjustment() && modelValue.recommendedPrice != null
            ? {
                attractivePrice: modelValue.attractivePrice ?? undefined,
                recommendedPrice: modelValue.recommendedPrice,
                heartStrokePrice: modelValue.heartStrokePrice ?? undefined,
              }
            : undefined,
      },
      message: modelValue.message,
    };
  }

  onSubmit(): void {
    if (this.isSubmitDisabled()) {
      return;
    }
    if (this.formTree().valid()) {
      const dto = this.buildDtoFromModel(this.model());
      this.submitForm.emit(dto);
    }
  }

  onCancel(): void {
    this.cancelled.emit();
  }
}
